import type { Condition } from '@locus-taxy/encore-grid';
import { useApiQuery } from '@locus-taxy/encore-ui-network';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import type { ApiError } from '@locus-taxy/encore-ui-network';
import type { CustomAppConfig } from '../../utils/customApp';

export interface EntityAction {
  enable: boolean;
  completionAction?: {
    type: string;
    [key: string]: unknown;
  };
  form?: {
    schema?: unknown;
    [key: string]: unknown;
  };
  /** Optional custom app config — when present, the action renders an MFE instead of a Camunda form */
  customApp?: CustomAppConfig;
  [key: string]: unknown;
}

/** One sort specification on a view. `direction` is a free-form string on the wire. */
export interface EntityViewSortSpec {
  field: string;
  direction: string;
}

/** Aggregation shown on a tab's count chip. */
export interface EntityViewAggregation {
  type: string;
  field: string;
  alias: string;
}

/**
 * One tab of a view. Recursive as of DP-3991 — `tabs` holds further tabs, so
 * nesting depth is a property of the data, not of the type.
 */
export interface EntityViewTabConfig {
  id: string;
  name: string;
  filter?: Condition;
  aggregation?: EntityViewAggregation;
  tabs?: EntityViewTabConfig[];
  /** @deprecated DP-3991 — superseded by the recursive `tabs`. Dropped in Phase C. */
  subTabs?: Array<{
    id: string;
    name: string;
    filter?: Condition;
    aggregation?: EntityViewAggregation;
  }>;
}

/** A column the grid draws for a view. `visible` is absent when unspecified. */
export interface EntityViewSelectedColumn {
  field: string;
  visible?: boolean;
}

/** The closed 8-token DLS status-chip colour palette (`ChipColor` on the
 * backend, `ChipProps['color']` in disco-ui). Kept as its own alias — same
 * decoupling rationale as encore-grid's identical alias — so
 * `fixedColor`/`resolvedStyle` values can be typed without a disco-ui import
 * here. */
export type EntityViewMapChipColor =
  | 'default'
  | 'primary'
  | 'muted'
  | 'info'
  | 'success'
  | 'caution'
  | 'warning'
  | 'destructive';

/** Same `{label, color, icon}` shape a grid column's
 * `additionalInfo.enumProperties` already uses (backend `EnumProperty`). */
export interface EntityViewEnumProperty {
  label?: string;
  color?: EntityViewMapChipColor;
  icon?: string;
}

/** A map layer's schema-derived capacity for viewport (bbox) filtering
 * (DP-3303 §7.3.3). `spatial`/`latLng` can be scoped to the map viewport;
 * `none` cannot. */
export type EntityViewMapBboxCapability = 'spatial' | 'latLng' | 'none';

/**
 * The real `DetailSection.kind` enum served on a map layer's
 * `relatedSections` (click-to-inspect popover). Only `'details'` has a
 * confirmed-available resolver in this workspace today — `relatedDetails` /
 * `relatedGrid` / `custom` need `useRelatedDetailsResolution` /
 * `useRelatedGridResolution` from `@locus-taxy/entity-detail`, which is
 * unvendored here and unconfirmed (mirrors encore-grid's own
 * `DetailSectionKind`, same precedent, same caveat).
 */
export type EntityViewDetailSectionKind =
  | 'details'
  | 'relatedDetails'
  | 'relatedGrid'
  | 'custom';

export interface EntityViewDetailSection {
  kind: EntityViewDetailSectionKind;
  title?: string;
  /** Bare dotted field paths (only meaningful for `kind: 'details'`). */
  properties?: string[];
  [key: string]: unknown;
}

/**
 * One map layer as served by the BFF (DP-3303) — mirrors
 * `EntityGridMetadataResponse.MapLayerResponse` field-for-field (and
 * encore-grid's identical `MapLayerResponse` TS mirror). `path`
 * empty/absent draws the view's own root entity; non-empty draws a related
 * entity reached via Endpoint B (`related-projection`).
 */
export interface EntityViewMapLayer {
  name?: string;
  path?: Array<Record<string, unknown>>;
  targetEntity?: string;
  field: string;
  geometryType: 'point' | 'line' | 'polygon';
  colorBy?: string;
  iconBy?: string;
  fixedColor?: EntityViewMapChipColor;
  minZoom?: number;
  maxZoom?: number;
  cluster?: boolean;
  relatedSections?: EntityViewDetailSection[];
  bboxCapability: EntityViewMapBboxCapability;
  /** Only present for a cross-entity layer (`path` non-empty) — the target
   * entity's `colorBy` palette inlined server-side. An own-field layer's
   * palette is already on this view's own `selectedColumns`. */
  resolvedStyle?: { values: Record<string, EntityViewEnumProperty> } | null;
}

/** Map-framework presentation for a view (DP-3303), served on
 * `ViewResponse.map` — absent when the view has no `map` block of its own
 * (unlike `card`, a map has no entity-wide default to fall back to). */
export interface EntityViewMapPresentation {
  layers?: EntityViewMapLayer[];
}

/**
 * One view as served by the BFF (`ViewResponse`). Carries resolved values —
 * `primary` is derived from `views.primaryViewId`, and `selectedColumns` is flat
 * here even though storage nests it under the view's grid presentation block.
 *
 * Per-view `card` is deliberately NOT modeled here (DP-3303 investigation,
 * this session): the BFF's `ViewResponse.card` (DP-4005, resolved: own `card`
 * or `viewDefaults.card`) is real, but `encore-grid` has not migrated its
 * mobile-card rendering (`resolveMobileCard`) to read it yet — it still reads
 * only the legacy flat `frontend.mobileCard` polyfill (see
 * {@link EntityMetadataResponse}, which DOES model that field: it has a live
 * consumer today). Modeling the per-view field here first would be ahead of
 * the one real consumer that exists.
 */
export interface EntityViewResponse {
  id: string;
  name: string;
  primary: boolean;
  filter?: Condition;
  sort?: EntityViewSortSpec[];
  tabs?: EntityViewTabConfig[];
  selectedColumns?: EntityViewSelectedColumn[];
  /** Map-framework presentation for this view (DP-3303), served on
   * `ViewResponse.map` — absent when the view has no `map` block. */
  map?: EntityViewMapPresentation;
}

/** The view set served to the frontend (`ViewsResponse`, DP-3991). */
export interface EntityViewsResponse {
  primaryViewId?: string;
  items: EntityViewResponse[];
}

/**
 * The `mobileCard.slots` position → field-key mapping (mirrors encore-grid's
 * own `MobileCardSlots`, `resolveMobileCard.tsx`). Every value names a grid
 * column; the mobile card view renders it by that column's own type.
 */
export interface EntityViewMobileCardSlots {
  title?: string;
  subtitle?: string;
  topRight?: string;
  botRight?: string;
  metaFields?: string[][];
  bodyFields?: string[][];
}

/** Mobile card-view config (DSCO-4310/DP-4005), served at
 * `frontend.mobileCard` — set per-entity in entity view config, absent until
 * configured (the card view then falls back to name/id defaults). */
export interface EntityViewMobileCardConfig {
  slots?: EntityViewMobileCardSlots;
}

export interface EntityMetadataResponse {
  data: {
    entitySchema: unknown;
    /**
     * The usecase (DP-3570) whose view-config document served this grid config,
     * or null/absent when the default document (or schema-derived config)
     * served — including when an unknown/invalid usecase was requested and fell
     * back to the default. A `resolvedUsecase` that differs from the requested
     * usecase is the signal for the non-blocking "showing default view" notice
     * (FR-20). Omitted on pre-feature responses.
     */
    resolvedUsecase?: string | null;
    /**
     * True only when the details block was served from the default document
     * while `resolvedUsecase` names a usecase (block-level fallback, FR-30).
     * By-design observability flag — NOT user-facing. Omitted on the common
     * (false) path.
     */
    detailsFellBack?: boolean;
    frontend: {
      /**
       * Entity-level status field path, merged from the entityViewConfig
       * entityPresentation overlay onto the schema value (see DP-3264).
       * Serialized only when non-null; `entityData.`-prefixed. Falls back to
       * the raw schema block when absent (older BE).
       */
      statusFieldPath?: string;
      subStatusFieldPath?: string;
      /**
       * View-scoped saved views (DP-3991). The new home for the view set; the
       * BE emits it alongside the deprecated `gridView.views` through this
       * phase, so read this one and fall back to `gridView.views` when absent.
       * Serialized only when non-null.
       */
      views?: EntityViewsResponse;
      /**
       * Mobile card-view config (DSCO-4310/DP-4005). Legacy flat placement —
       * `encore-grid`'s own mobile-card renderer (`resolveMobileCard`) reads
       * only this field today, not the newer per-view `ViewResponse.card`
       * (see the note on {@link EntityViewResponse}). Backend repopulates it
       * from the primary view's resolved `card` as a backward-compat
       * polyfill; absent until the entity has one configured.
       */
      mobileCard?: EntityViewMobileCardConfig;
      gridView: {
        /**
         * Record-access gate (DSCO-5543). `false` disables filtering by the
         * active access dimension; `true` or absent applies it.
         */
        applyRecordAccessFilter?: boolean;
        /**
         * Deprecated predecessor of {@link applyRecordAccessFilter}, and still
         * the ONLY key deployed backends send today. Read as a same-value alias
         * for one release (HLD §B); dropping it hid every per-entity opt-out.
         */
        applyTeamFilter?: boolean;
        columns: Array<{
          label: string;
          field: string;
          [key: string]: unknown;
        }>;
        actions?: {
          create?: EntityAction;
          import?: EntityAction;
          export?: EntityAction;
          [key: string]: unknown;
        };
        quickFilterColumns?: string[];
        globalSearchEnabledColumns?: string[];
        /**
         * @deprecated DP-3991 — moved to `frontend.views`. Still populated with
         * the same list through this phase, then dropped in Phase C. Omitted
         * when empty.
         */
        views?: EntityViewResponse[];
      };
      detailsView?: {
        form?: {
          schema: unknown;
        };
        timeline?: {
          visible: boolean;
        };
        app?: CustomAppConfig;
      };
    };
    creationForm?: {
      schema: unknown;
    };
    [key: string]: unknown;
  };
  success: boolean;
}

export function useEntityMetadata(
  clientId: string,
  entityName: string,
  options?: { includeResolvedSchema?: boolean; usecase?: string }
) {
  const { showApiError } = useToast();
  const usecase = options?.usecase;

  return useApiQuery<EntityMetadataResponse>({
    // The usecase (DP-3570) joins the query key only when present, so
    // default-page callers keep their existing key byte-for-byte while a
    // usecase page resolves under a distinct key — navigating default ↔
    // usecase for one entity never serves the other's cached metadata.
    key: [
      'entityMetadata',
      clientId,
      entityName,
      options?.includeResolvedSchema ? 'withSchema' : 'basic',
      ...(usecase ? [usecase] : []),
    ],
    endpointKey: 'entity.metadata',
    config: {
      enabled: !!entityName && entityName.trim() !== '',
      staleTime: 0,
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
    // `usecase` is not a path placeholder, so the network layer appends it as a
    // `?usecase=` query param. Spread conditionally to avoid sending an empty
    // param on the default path.
    params: {
      clientId,
      entityName,
      ...(options?.includeResolvedSchema && { include: 'resolvedSchema' }),
      ...(usecase ? { usecase } : {}),
    },
  });
}
